import {
	createContext,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from "react";
import {
	applyThemeToDocument,
	getStoredTheme,
	setStoredTheme,
	type Theme,
} from "@/utils/theme";

export interface ThemeContextInterface {
	theme: Theme;
	darkTheme: boolean;
	toggleTheme(): void;
}

export const ThemeContext = createContext<ThemeContextInterface | null>(null);

export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
	// main.tsx applies stored theme right after hydrateRoot; keep state in sync.
	const [theme, setTheme] = useState<Theme>(() => getStoredTheme());

	useEffect(() => {
		document.documentElement.classList.add("theme-transition");
		document.body.classList.add("theme-transition");
		return () => {
			document.documentElement.classList.remove("theme-transition");
			document.body.classList.remove("theme-transition");
		};
	}, []);

	// Toggle between dark and light theme
	const toggleTheme = useCallback(() => {
		const newTheme: Theme = theme === "dark" ? "light" : "dark";
		setStoredTheme(newTheme);
		setTheme(newTheme);
		applyThemeToDocument(newTheme);
	}, [theme]);

	// Listen for storage changes (cross-tab sync)
	useEffect(() => {
		const handleStorageChange = (e: StorageEvent) => {
			if (e.key === "theme" && e.newValue) {
				const newTheme = e.newValue as Theme;
				if (newTheme === "dark" || newTheme === "light") {
					setTheme(newTheme);
					applyThemeToDocument(newTheme);
				}
			}
		};

		window.addEventListener("storage", handleStorageChange);

		return () => {
			window.removeEventListener("storage", handleStorageChange);
		};
	}, []);

	const contextValue = useMemo(
		() => ({
			theme,
			darkTheme: theme === "dark",
			toggleTheme,
		}),
		[theme, toggleTheme],
	);

	return (
		<ThemeContext.Provider value={contextValue}>
			{children}
		</ThemeContext.Provider>
	);
};
